import axiosInstance from '../../core/axiosConfig.ts';
import * as Sentry from '@sentry/react';

/**
 * Where customers come from and what they agreed to: partners (hotels, hosts
 * and others who send us customers), their monthly statements, the
 * acquisition report, and a customer's own touches and consent records.
 * Admins only; the backend enforces the same on every /admin endpoint here.
 * The shapes follow work/acquisition-consent-contract.md, sections 3.1-3.3.
 */

export const PARTNER_TYPES = ['HOTEL', 'HOST', 'GUIDE', 'AGENCY', 'INFLUENCER', 'OTHER'] as const;
export type PartnerType = typeof PARTNER_TYPES[number];

export type Partner = {
    id: number;
    code: string;
    name: string;
    partnerType: PartnerType;
    ratePercent: number;
    attributionDays: number;
    contactName: string | null;
    contactPhone: string | null;
    contactEmail: string | null;
    notes: string | null;
    isActive: boolean;
    createdAt: string;
    /** "https://thevello.com/join/HILTON" */
    link: string;
    /** All-time figures. */
    customers: number;
    deliveredOrders: number;
    foodSales: number;
    commission: number;
};

export type StatementOrder = {
    orderId: number;
    orderNumber: string;
    deliveredAt: string;
    customerId: number;
    customerName: string | null;
    locationName: string | null;
    foodSubtotal: number;
    commission: number;
};

export type PartnerStatement = {
    partner: Partner;
    month: string;
    orders: StatementOrder[];
    totals: { orders: number; foodSales: number; commission: number };
};

export type ReportBy = 'source' | 'campaign' | 'partner' | 'channel';

export type AcquisitionReportRow = {
    key: string;
    signups: number;
    ordered: number;
    deliveredOrders: number;
    foodSales: number;
    /** 0-1 */
    firstOrderRate: number;
};

export type AcquisitionReport = {
    from: string;
    to: string;
    by: ReportBy;
    rows: AcquisitionReportRow[];
    totals: { signups: number; ordered: number; deliveredOrders: number; foodSales: number };
};

export type Touch = {
    id: number;
    channel: string | null;
    kind: string | null;
    source: string | null;
    medium: string | null;
    campaign: string | null;
    content: string | null;
    partnerCode: string | null;
    partnerName: string | null;
    referrerHost: string | null;
    landingPath: string | null;
    createdAt: string;
};

export type ConsentRecord = {
    type: string;
    granted: boolean;
    policyVersion: string | null;
    channel: string | null;
    appVersion: string | null;
    createdAt: string;
};

export type MemberAcquisition = {
    firstTouch: Touch | null;
    partner: { id: number; code: string; name: string } | null;
    partnerAttribution: 'LINK' | 'CODE' | 'ADMIN' | null;
    partnerAttributedAt: string | null;
    touches: Touch[];
    /** Newest first, every record: the full audit trail. */
    consents: ConsentRecord[];
};

/** What the QR code on a partner's counter card opens. */
export const partnerQrUrl = (code: string) =>
    `https://thevello.com/join/${encodeURIComponent(code)}?utm_source=partner&utm_medium=qr&utm_campaign=${encodeURIComponent(code.toLowerCase())}`;

const report = (source: string) => (e: unknown) => {
    Sentry.captureException(e, { tags: { source } });
    throw e;
};

export default class MarketingService {
    /** Every partner, by name, for the pickers. Inactive ones too: their history still counts. */
    async partners(): Promise<Partner[]> {
        return axiosInstance
            .get('/admin/partners', { params: { _start: 0, _end: 1000, _sort: 'name', _order: 'ASC' } })
            .then((r) => r.data ?? [])
            .catch(report('partners-list'));
    }

    /** month is yyyy-MM. */
    async statement(partnerId: number, month: string): Promise<PartnerStatement> {
        return axiosInstance
            .get(`/admin/partners/${partnerId}/statement`, { params: { month } })
            .then((r) => r.data)
            .catch(report('partner-statement'));
    }

    /** from and to are inclusive Cyprus dates, yyyy-MM-dd. */
    async report(from: string, to: string, by: ReportBy): Promise<AcquisitionReport> {
        return axiosInstance
            .get('/admin/acquisition/report', { params: { from, to, by } })
            .then((r) => r.data)
            .catch(report('acquisition-report'));
    }

    async memberAcquisition(memberId: number): Promise<MemberAcquisition> {
        return axiosInstance
            .get(`/admin/member/${memberId}/acquisition`)
            .then((r) => r.data)
            .catch(report('member-acquisition'));
    }

    /** Sets (attribution "ADMIN") or, with null, clears the customer's partner. */
    async setMemberPartner(memberId: number, partnerId: number | null): Promise<void> {
        await axiosInstance
            .put(`/admin/member/${memberId}/partner`, { partnerId })
            .catch(report('member-partner'));
    }
}
